import { useMemo } from 'preact/hooks';

const INK_COLOR = {
  cyan: '#00B4D8', magenta: '#E63B7A', yellow: '#F4C430',
  black: 'var(--color-text-primary)', white: 'var(--color-text-secondary)', spot: '#A18AF5',
};

// Dot-gain anchor table (mirror of server/dot-gain.js — kept client-side for live display)
const ANCHORS = [
  [0.4,1.0],[0.8,2.0],[1.0,3.0],[3.0,9.0],[5.0,13.0],[10.0,22.0],[20.0,37.0],[30.0,51.0],
  [40.0,62.0],[50.0,72.0],[60.0,81.0],[70.0,88.0],[80.0,93.0],[90.0,97.0],[95.0,99.0],[100.0,100.0],
];
const interpolateTarget = (label) => {
  const s = parseFloat(label);
  if (!Number.isFinite(s) || s < ANCHORS[0][0] || s > ANCHORS[ANCHORS.length - 1][0]) return null;
  for (const [as, at] of ANCHORS) if (Math.abs(as - s) < 1e-9) return at;
  for (let i = 0; i < ANCHORS.length - 1; i += 1) {
    const [s0, t0] = ANCHORS[i];
    const [s1, t1] = ANCHORS[i + 1];
    if (s >= s0 && s <= s1) return t0 + ((s - s0) / (s1 - s0)) * (t1 - t0);
  }
  return null;
};

const cellInput = 'w-full rounded border border-border-subtle bg-bg-card px-1.5 py-1 text-center font-mono text-[12px] text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const cellReadOnly = 'w-full rounded border border-border-subtle bg-bg-base px-1.5 py-1 text-center font-mono text-[12px] text-text-tertiary';

const fmt = (v, dp) => (v == null || !Number.isFinite(Number(v)) ? '' : Number(v).toFixed(dp));

export const DensityGrid = ({ weight, inks, stepLabels, onChange, readOnly = false }) => {
  // Column-major tabindex: D + step rows (excluding "100") for inkA, then inkB, etc.
  // Compute tabindex per (rowKind, stepIndex, inkIndex)
  // D row tabindex = 1 + inkIndex * (stepRowCount + 1)
  // Step row "label X" at stepIndex tabindex = 1 + inkIndex * (stepRowCount + 1) + 1 + stepIndex (skipping "100")
  const tabMap = useMemo(() => {
    const map = new Map(); // key "row-col" → tabindex
    const tabbableSteps = stepLabels.map((l, i) => ({ label: l, idx: i })).filter(({ label }) => String(label) !== '100');
    const perColumn = 1 + tabbableSteps.length; // D + non-100 steps
    inks.forEach((_, ci) => {
      map.set(`D-${ci}`, 1 + ci * perColumn);
      tabbableSteps.forEach(({ idx }, ti) => {
        map.set(`S${idx}-${ci}`, 1 + ci * perColumn + 1 + ti);
      });
    });
    return map;
  }, [inks, stepLabels]);

  const updateDensity = (ci, val) => {
    const density = [...(weight.density || inks.map(() => null))];
    while (density.length < inks.length) density.push(null);
    density[ci] = val === '' ? null : Number(val);
    onChange({ ...weight, density });
  };

  const updateStep = (si, ci, val) => {
    const steps = (weight.steps || []).map((r) => r ? [...r] : inks.map(() => null));
    while (steps.length < stepLabels.length) steps.push(inks.map(() => null));
    while (steps[si].length < inks.length) steps[si].push(null);
    steps[si][ci] = val === '' ? null : Number(val);
    onChange({ ...weight, steps });
  };

  const cmykIdx = inks.map((ink, i) => ({ ink, i }))
    .filter(({ ink }) => ['cyan','magenta','yellow','black'].includes(ink.kind))
    .map(({ i }) => i);
  const showAvgDev = cmykIdx.length >= 2;

  return (
    <div>
      <p class="mb-1.5 flex items-center gap-1 text-[10px] text-text-tertiary">
        <span>⇣ Tab order: D → 95 → 90 → … → 1 down C, then jump to M's D. 100% row is read-only.</span>
      </p>
      <table class="w-full border-separate" style={{ borderSpacing: '2px', fontSize: '11px' }}>
        <thead>
          <tr>
            <th class="w-9 px-1 py-1 text-left text-text-tertiary">Step</th>
            {inks.map((ink) => (
              <th key={ink.name} class="px-1 py-1 text-center font-semibold" style={{ color: INK_COLOR[ink.kind] ?? '#A18AF5' }}>{ink.name}</th>
            ))}
            {showAvgDev && <>
              <th class="w-14 px-1 py-1 text-center text-[10px] text-text-tertiary">Target</th>
              <th class="w-14 px-1 py-1 text-center text-[10px] text-text-tertiary">Avg</th>
              <th class="w-14 px-1 py-1 text-center text-[10px] text-text-tertiary">Δ</th>
            </>}
          </tr>
        </thead>
        <tbody>
          {/* D row */}
          <tr style={{ background: 'rgba(245,238,220,0.06)' }}>
            <td class="px-1 py-1 font-mono font-semibold text-text-primary">D</td>
            {inks.map((_, ci) => (
              <td key={ci}>
                <input
                  type="number" step="0.01"
                  class={readOnly ? cellReadOnly : cellInput}
                  style={{ background: 'rgba(245,238,220,0.10)' }}
                  value={weight.density?.[ci] ?? ''}
                  onInput={(e) => updateDensity(ci, e.currentTarget.value)}
                  readOnly={readOnly}
                  tabIndex={readOnly ? -1 : tabMap.get(`D-${ci}`)}
                />
              </td>
            ))}
            {showAvgDev && <>
              <td class="text-center text-text-tertiary">—</td>
              <td class="text-center text-text-tertiary">—</td>
              <td class="text-center text-text-tertiary">—</td>
            </>}
          </tr>

          {/* Step rows */}
          {stepLabels.map((label, si) => {
            const isHundred = String(label) === '100';
            const rowVals = isHundred
              ? inks.map(() => 100)
              : inks.map((_, ci) => weight.steps?.[si]?.[ci] ?? null);
            const cmykVals = cmykIdx.map((i) => rowVals[i]).filter((v) => v != null && Number.isFinite(v));
            const avg = cmykVals.length ? cmykVals.reduce((a, b) => a + b, 0) / cmykVals.length : null;
            const target = interpolateTarget(label);
            const dev = (avg != null && target != null) ? avg - target : null;
            const devStr = dev == null ? '' : (Math.abs(dev) < 0.05 ? '0' : (dev >= 0 ? '+' : '') + dev.toFixed(1));
            const devColor = dev != null && Math.abs(dev) > 3 ? '#E63B7A' : 'var(--color-text-secondary)';

            return (
              <tr key={si}>
                <td class="px-1 py-1 font-mono text-text-tertiary">{label}</td>
                {inks.map((_, ci) => (
                  <td key={ci}>
                    <input
                      type="number" step="0.1"
                      class={isHundred || readOnly ? cellReadOnly : cellInput}
                      value={isHundred ? 100 : (weight.steps?.[si]?.[ci] ?? '')}
                      onInput={(e) => !isHundred && updateStep(si, ci, e.currentTarget.value)}
                      readOnly={isHundred || readOnly}
                      tabIndex={(isHundred || readOnly) ? -1 : tabMap.get(`S${si}-${ci}`)}
                    />
                  </td>
                ))}
                {showAvgDev && <>
                  <td class="text-center font-mono text-text-tertiary">{target != null ? target.toFixed(1) : ''}</td>
                  <td class="text-center font-mono">{fmt(avg, 1)}</td>
                  <td class="text-center font-mono" style={{ color: devColor }}>{devStr}</td>
                </>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
